/**
 * ShareInternallyDialog — grant people INSIDE this drive access to an item.
 *
 * Assembled from the EXISTING pieces rather than new ones:
 *   - the ShareDialog shell ("+ Add recipient" → staged "Share with" cards →
 *     Cancel / Share footer),
 *   - `AddPicker` (WhoHasAccessDialog's Role / Team / Member picker) for
 *     choosing who,
 *   - `RecipientAccessPicker` (ShareDialog's permission + re-sharing popover)
 *     for each card's access.
 *
 * Writes go through the same `POST /api/folders/:id/permissions` endpoint
 * WhoHasAccessDialog uses (targetType role|group|user, plus allowSharing), so
 * the server applies exactly the same gating.
 *
 * Rendered OUTSIDE a ThemedArea (portal), so it applies the theme class itself.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Plus, Loader2, Folder, File } from 'lucide-react'
import { request, pinnedBatch } from '@papan/api-client'
import { useTranslation } from '@papan/i18n'
import { useTheme, getThemeClass, useToast, useDraftState } from '@papan/core-ui/ui'
import { RecipientAccessPicker, type SharePermission } from '@papan/org-connections/ui'
import { AddPicker } from './WhoHasAccessDialog.AddPicker.js'
import { CardAvatar } from './WhoHasAccessDialog.js'
import type { MemberInfo, Permission, TagType, TeamInfo } from './WhoHasAccessDialog.types.js'
import { ROLE_LABELS } from './WhoHasAccessDialog.types.js'

interface Staged {
  key: string
  type: TagType
  targetId: string
  label: string
  permission: SharePermission
  allowSharing: boolean
  /** Already granted on the server (loaded on open) — shown from the start,
   *  and a permission change re-POSTs instead of adding a new grant. */
  existing?: boolean
  /** folder_permissions row id — present for existing rows; needed to revoke. */
  permId?: string
  /** The permission as granted on the server — what a change is measured
   *  against, so re-picking it is not a change (`initialSharing` is the same
   *  for allow_sharing). Existing rows only. */
  origPermission?: SharePermission
  /** An existing row whose permission/sharing the user changed in this session. */
  dirty?: boolean
}

/** Sentinel tier used only to make AddPicker's already-added check fire. */
/** One instance, so the drafts' empty fallback is stable across renders. */
const EMPTY_STAGED: Staged[] = []

const PICKER_PERM = 'readcopy' as Permission

/** Internal grants legitimately include admin (unlike outsider shares). */
const INTERNAL_TIERS: SharePermission[] = ['readcopy', 'readwrite', 'admin']

/** Staged picks first ("who am I about to share with"), then current access. */
const SECTIONS = [
  { key: 'new' as const, labelKey: 'driveView.shareInternally.sectionNew', fallback: 'Sharing with' },
  { key: 'existing' as const, labelKey: 'driveView.shareInternally.sectionExisting', fallback: 'Already has access' },
]

interface PermissionsResponse {
  permissions: Array<{ id: string; targetType: 'user' | 'role' | 'group'; targetId: string; permission: string }>
  allowSharing?: Record<string, boolean>
  /** One of the drive's own folders (Library, Projects…): sharing it is off by default
   *  and its setting lives ON the folder, not org-wide (owner ruling 2026-10-02). */
  structuralFolder?: string
}

export function ShareInternallyDialog({ fileId, fileName, tenantId, isOrg, isFolder = true, onClose }: {
  fileId: string
  fileName: string
  tenantId: string
  /** Personal drives have no roles/teams/members — the dialog says so. */
  isOrg?: boolean
  isFolder?: boolean
  /** Accepted for call-site symmetry with the other share dialogs; the tier
   *  menu is deliberately NOT capped here (the server is the gate). */
  myPermission?: string
  onClose: () => void
}) {
  const { t } = useTranslation('files')
  const toast = useToast()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  const [teams, setTeams] = useState<TeamInfo[]>([])
  const [members, setMembers] = useState<MemberInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [picking, setPicking] = useState(false)
  /** The "+ Add member" button, captured from the click that opens the picker:
   *  AddPicker positions its desktop popover against it (#1240 — without it the
   *  popover never showed on a desktop). */
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null)
  // #897 — the staged list and the removals are kept across a phone/desktop
  // layout swap (keyed by tenant + folder). The load below seeds the list from
  // the server only when there is no draft yet, so a restored one survives it.
  const draftBase = `shareInt:${tenantId}:${fileId}`
  const [staged, setStaged, { hasDraft: stagedHasDraft }] = useDraftState<Staged[]>(`${draftBase}:staged`, EMPTY_STAGED)
  /** Existing grants the user removed in this session — revoked on Share. */
  const [removed, setRemoved, { hasDraft: removedHasDraft }] = useDraftState<Staged[]>(`${draftBase}:removed`, EMPTY_STAGED)
  /** A draft existed when the dialog mounted: a swap restored it. EITHER one —
   *  every grant removed leaves the staged list empty, which equals its
   *  fallback and so is no draft; the removals are what say it was edited. */
  const stagedRestoredRef = useRef(stagedHasDraft || removedHasDraft)
  /** allow_sharing as loaded, per principal. The capability is TENANT-GLOBAL,
   *  so only a deliberate change may be written — see commit(). */
  const initialSharing = useRef(new Map<string, boolean>())
  const [sharing, setSharing] = useState(false)
  /** One of the drive's own folders: "allow sharing" is set on THIS folder (with the
   *  grant), defaults to off, and the org-wide value doesn't apply to it. */
  const [sharingOnFolder, setSharingOnFolder] = useState(false)

  useEffect(() => {
    if (!isOrg) { setLoading(false); return }
    let alive = true
    Promise.all([
      request<{ teams: TeamInfo[] }>(`/api/orgs/${tenantId}/teams`).catch(() => ({ teams: [] as TeamInfo[] })),
      // `{ members, invites }` — BOTH, for the same reason WhoHasAccessDialog
      // takes both: this dialog writes through the identical
      // `POST /api/folders/:id/permissions`, which accepts a pending invitee as
      // a grant target so access can be staged before they accept (#112).
      // Reading `members` alone made an invitee grantable from the folder page
      // and not from here — the very asymmetry this was meant to remove.
      request<{ members: MemberInfo[]; invites?: MemberInfo[] }>(`/api/orgs/${tenantId}/members`)
        .catch(() => ({ members: [] as MemberInfo[], invites: [] as MemberInfo[] })),
      request<PermissionsResponse>(`/api/folders/${fileId}/permissions`).catch(() => ({ permissions: [] } as PermissionsResponse)),
    ]).then(([tr, mr, pr]) => {
      if (!alive) return
      setSharingOnFolder(!!pr.structuralFolder)
      const teamList = tr.teams ?? []
      // Declined invites excluded: re-inviting clears that state first.
      const memberList = [
        ...(mr.members ?? []).map(m => ({ ...m, status: m.status ?? ('active' as const) })),
        // Lowercased for the same reason as WhoHasAccessDialog: the server
        // stores user grant targets normalised, so client state must match.
        ...(mr.invites ?? []).filter(i => i.status !== 'declined')
          .map(i => ({ ...i, email: i.email.toLowerCase(), status: 'invited' as const })),
      ]
      setTeams(teamList)
      setMembers(memberList)
      // Seed the "Share with" list with who ALREADY has explicit access, so the
      // dialog shows the current audience instead of pretending it's empty.
      const teamName = new Map(teamList.map(x => [x.id, x.name]))
      const memberName = new Map(memberList.map(m => [m.email.toLowerCase(), m.name?.trim() || m.email]))
      // `hidden` ('No access') is a DENIAL, not a share. This dialog can't
      // express it (INTERNAL_TIERS omits it), so a seeded hidden row rendered
      // with an empty tier label under the heading "Already has access" —
      // exactly backwards. Leave denials to the Who-has-access dialog.
      const explicit = (pr.permissions ?? []).filter(p => p.permission !== 'hidden')
      const seeded = explicit.map(p => {
        const type: TagType = p.targetType === 'group' ? 'team' : p.targetType === 'role' ? 'role' : 'member'
        // Same resolution as `displayName` (which can't be used here — it
        // closes over state this effect is still populating).
        const label = type === 'role' ? (ROLE_LABELS[p.targetId] ?? p.targetId)
          : type === 'team' ? (teamName.get(p.targetId) ?? p.targetId)
            : (memberName.get(p.targetId.toLowerCase()) ?? p.targetId)
        const sharingKey = `${type === 'member' ? 'member' : type}:${p.targetId}`
        const allowSharing = pr.allowSharing?.[sharingKey] ?? !pr.structuralFolder
        initialSharing.current.set(`${type}:${p.targetId}`, allowSharing)
        return {
          key: `${type}:${p.targetId}`,
          type,
          targetId: p.targetId,
          label,
          permission: p.permission as SharePermission,
          origPermission: p.permission as SharePermission,
          allowSharing,
          existing: true,
          permId: p.id,
        } satisfies Staged
      })
      // The baseline above is always the server's; the LIST is seeded only
      // without a draft (a swap restored one — keep the user's changes).
      if (!stagedRestoredRef.current) setStaged(seeded)
      setLoading(false)
    })
    return () => { alive = false }
  }, [tenantId, isOrg, fileId])

  useEffect(() => {
    // Not while a batch is in flight: unmounting mid-commit fires the caller's
    // onClose (which refreshes the drive) before the POSTs land, showing a
    // half-applied state, and toasts from an unmounted tree.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sharing) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, sharing])

  // Only NEW rows and CHANGED existing rows are written; untouched existing
  // grants are left alone (re-POSTing them would be a no-op write per row).
  const pending = staged.filter(s => !s.existing || s.dirty)
  // A principal removed and then re-added in the same session is a CHANGE, not
  // a revoke: `fpRepo.set` upserts on (tenant, folder, targetType, targetId)
  // and keeps the row id, so the POST would update the very row the DELETE
  // then deletes — silently wiping the access we just granted. Anything back
  // in `staged` therefore drops out of the revoke list. Derived here (not
  // inside commit) so the submit button can gate on the same list.
  const revokes = useMemo(() => {
    const stagedKeys = new Set(staged.map(s => s.key))
    return removed.filter(r => !stagedKeys.has(r.key) && r.permId)
  }, [staged, removed])

  const commit = async () => {
    if (pending.length === 0 && revokes.length === 0) return
    // #1316 review round 2 (owner, 2026-10-28): the Share the person pressed FINISHES as the identity that pressed it —
    // every grant / revoke is sent as it (`batch.opts()`), never as the next session; nothing is shown after its session
    const batch = pinnedBatch()
    setSharing(true)
    let granted = 0
    let revoked = 0
    let firstError = ''
    // Rows that landed, so a retry after a PARTIAL failure doesn't re-send them
    // (a second DELETE of the same permId 404s, which would wedge the dialog:
    // it only closes when the batch is error-free).
    /** Per landed grant: the server's row id and the permission SENT. */
    const doneGrants = new Map<string, { id: string | undefined; permission: SharePermission }>()
    /** Rows whose re-sharing write was REFUSED for good — a 4xx that a retry
     *  would only repeat: 403 (a folder-level admin always gets it), 404 (an
     *  invitee has no membership yet; a member/team gone since load), 400. A
     *  failure a retry can fix (network, timeout, 5xx, 408, 429) leaves the row
     *  unsaved so Share can retry it. */
    const refusedSharing = new Set<string>()
    const doneRevokes = new Set<string>()
    for (const s of pending) {
      const targetType = s.type === 'role' ? 'role' : s.type === 'team' ? 'group' : 'user'
      try {
        const res = await request<{ permission?: { id?: string } }>(`/api/folders/${fileId}/permissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // On one of the drive's own folders the sharing setting rides the grant
          // (folder-scoped, as Who has access writes it) — the org-wide value below
          // doesn't apply there. Only a deliberate change is sent.
          body: JSON.stringify({
            targetType, targetId: s.targetId, permission: s.permission,
            ...(sharingOnFolder && s.allowSharing !== initialSharing.current.get(s.key) ? { allowSharing: s.allowSharing } : {}),
          }),
          ...batch.opts(),
        })
        granted++
        if (sharingOnFolder) initialSharing.current.set(s.key, s.allowSharing)
        // Keep the row id the server assigned. Without it a reconciled row has
        // no permId, and a later revoke of that same row is dropped on the
        // floor by the `r.permId` filter below — access the UI says is gone.
        doneGrants.set(s.key, { id: res?.permission?.id, permission: s.permission })
      } catch (e) {
        firstError ||= (e as Error)?.message || ''
        continue
      }
      // Re-sharing is a PRINCIPAL capability, not a row attribute — the grant
      // endpoint only honors `allowSharing` for apps. But the capability is
      // TENANT-GLOBAL: PATCHing it on every grant would silently re-enable
      // org-wide re-sharing for a role an admin had switched off, just because
      // someone shared one folder with that role. Only send a deliberate
      // CHANGE. It's also org-owner/admin-only, so a folder-level admin gets a
      // 403 here — that must not fail the grant, which already landed.
      if (!sharingOnFolder && s.allowSharing !== initialSharing.current.get(s.key)) {
        try {
          await request(`/api/orgs/${tenantId}/capabilities`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ targetType, targetId: s.targetId, allowSharing: s.allowSharing }),
            ...batch.opts(),
          })
          initialSharing.current.set(s.key, s.allowSharing)
        } catch (e) {
          firstError ||= (e as Error)?.message || ''
          const status = (e as { status?: number } | null)?.status
          if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429) refusedSharing.add(s.key)
        }
      }
    }
    for (const r of revokes) {
      try {
        await request(`/api/folders/${fileId}/permissions/${r.permId}`, { method: 'DELETE', ...batch.opts() })
        revoked++
        doneRevokes.add(r.key)
      } catch (e) {
        firstError ||= (e as Error)?.message || ''
      }
    }
    // The batch finished as its starter: the dialog belongs to the session that ended — nothing of it is shown
    if (batch.ended()) { setSharing(false); return }
    // Reconcile so the open dialog reflects what actually landed: granted rows
    // become clean existing ones, revoked rows leave the pending revoke list.
    if (doneGrants.size > 0) {
      setStaged(prev => prev.map(x => doneGrants.has(x.key)
        // Carry the server's row id through. A reconciled row without one is
        // un-revokable: the revoke list filters on `permId`, so removing it
        // later would drop silently while the UI showed it gone.
        //
        // Baseline what was SENT, and judge dirty the same way the picker does:
        // a pick made while Share was in flight, or a re-sharing write that
        // FAILED (network, 5xx) after its grant landed, stays unsaved instead of
        // reading as saved. A REFUSED one (a lasting 4xx) is put back to the
        // setting that stands: left showing, it kept the row unsaved and Share
        // retrying a write that can never succeed.
        ? (() => {
            const done = doneGrants.get(x.key)!
            const allowSharing = refusedSharing.has(x.key)
              ? (initialSharing.current.get(x.key) ?? x.allowSharing)
              : x.allowSharing
            return {
              ...x, allowSharing, existing: true, origPermission: done.permission, permId: done.id ?? x.permId,
              dirty: x.permission !== done.permission || allowSharing !== initialSharing.current.get(x.key),
            }
          })()
        : x))
    }
    if (doneRevokes.size > 0) setRemoved(prev => prev.filter(x => !doneRevokes.has(x.key)))
    // A self-lockout 403 leaves the row out of the list though the grant still
    // stands — put it back rather than assert a removal that didn't happen.
    const failedRevokes = revokes.filter(r => !doneRevokes.has(r.key))
    if (failedRevokes.length > 0) {
      setStaged(prev => [...prev, ...failedRevokes.filter(r => !prev.some(x => x.key === r.key))])
      setRemoved(prev => prev.filter(x => !failedRevokes.some(f => f.key === x.key)))
    }
    setSharing(false)
    const ok = granted + revoked
    if (ok > 0) {
      // Name the action that actually happened, from the rows that actually
      // LANDED — indexing the input arrays named the failing recipient when a
      // batch was partly rejected ("Shared with Alice" for the one that 403'd).
      const only = granted === 1 && revoked === 0
        ? pending.find(s => doneGrants.has(s.key))
        : revoked === 1 && granted === 0
          ? revokes.find(r => doneRevokes.has(r.key))
          : null
      toast.success(
        only
          ? (granted === 1
            ? t('driveView.shareInternally.granted', { name: only.label, defaultValue: `Shared with ${only.label}` })
            : t('driveView.shareInternally.revoked', { name: only.label, defaultValue: `Removed ${only.label}` }))
          : t('driveView.shareInternally.grantedMany', { count: ok, defaultValue: `Updated ${ok} recipients` }),
      )
    }
    if (firstError) toast.error(firstError)
    if (ok > 0 && !firstError) onClose()
  }

  /** Plain display name for a principal — no `role:` / `team:` tag prefix. */
  const displayName = (type: TagType, targetId: string): string => {
    if (type === 'role') return ROLE_LABELS[targetId] ?? targetId
    if (type === 'team') return teams.find(x => x.id === targetId)?.name ?? targetId
    return members.find(m => m.email.toLowerCase() === targetId.toLowerCase())?.name?.trim() || targetId
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Inert mid-commit, same reason as the Escape handler above. */}
      <div className="fixed inset-0 bg-black/50" onClick={() => { if (!sharing) onClose() }} />
      {/* `data-picker-bounds`: AddPicker keeps its popover inside THIS box
          rather than the viewport, the same marker WhoHasAccessDialog sets. */}
      <div className={`relative z-10 rounded-lg shadow-xl max-w-lg w-full ${themeClass} themed-bg-secondary`} data-testid="share-internally-dialog" data-picker-bounds="">
        {/* Header — same shape as ShareDialog's. */}
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="text-lg font-semibold themed-text flex items-center gap-1.5 min-w-0">
            <span>{t('driveView.shareInternally.title', { defaultValue: 'Share internally' })}</span>
            <span className="inline-flex items-center gap-1.5 themed-text-muted font-normal min-w-0">
              {/* Same glyph the sibling Share-to-another-Drive dialog uses. */}
              {isFolder
                ? <Folder size={18} className="text-yellow-500 shrink-0" />
                : <File size={18} className="themed-text-muted shrink-0" />}
              <span className="truncate">{fileName}</span>
            </span>
          </h3>
          {/* Disabled mid-commit, like Escape and the backdrop — closing while
              POSTs are in flight refreshes the caller against half-applied
              state and toasts from an unmounted tree. */}
          <button onClick={onClose} disabled={sharing} className="themed-text-muted hover:themed-text transition-colors shrink-0 disabled:opacity-40" aria-label={t('common.close', { defaultValue: 'Close' })}>
            <X size={18} />
          </button>
        </div>

        {!isOrg ? (
          <div className="px-6 py-8 text-sm themed-text-muted text-center">
            {t('driveView.shareInternally.personalOnly', { defaultValue: 'A personal drive has no roles, teams, or members. Use “Share link” or “Share to another Drive” instead.' })}
          </div>
        ) : (
          <>
            {/* + Add member — opens the SHARED AddPicker (Role / Team / Member
                tabs + search), the same control WhoHasAccessDialog uses. */}
            <div className="px-6 pt-4 relative">
              <button
                onClick={(e) => { setPickerAnchor(e.currentTarget); setPicking(o => !o) }}
                className="text-sm text-violet-400 hover:text-violet-300 transition-colors inline-flex items-center gap-1.5"
                data-testid="share-internally-add"
                disabled={loading}
              >
                <Plus size={15} />
                {t('driveView.shareInternally.addMember', { defaultValue: 'Add member' })}
              </button>

              {picking && (
                <AddPicker
                  activePermission={PICKER_PERM}
                  // Staged rows are passed as tags so the picker greys out what's
                  // already in the list. AddPicker only treats a tag as
                  // "already added" when its permission EQUALS activePermission
                  // (it's built for per-tier tabs), so every row is normalized to
                  // PICKER_PERM here — this dialog cares about presence, not tier.
                  tags={staged.map(s => ({
                    type: s.type,
                    targetId: s.targetId,
                    label: s.label,
                    permission: PICKER_PERM,
                    inherited: false,
                  }))}
                  allTeams={teams}
                  allMembers={members}
                  initialTab="member"
                  anchorEl={pickerAnchor}
                  // No tier here (every row is staged at PICKER_PERM), so the
                  // picker is named after the button: the phone sheet's title
                  // and Close button, and its accessible name everywhere.
                  title={t('driveView.shareInternally.addMember', { defaultValue: 'Add member' })}
                  closeLabel={t('common:close', { defaultValue: 'Close' })}
                  onPick={(type, targetId) => {
                    // AddPicker hands back WhoHasAccess's PREFIXED tag label
                    // ("role:Member", "team:Design") — resolve the plain display
                    // name here instead, matching the seeded rows.
                    const label = displayName(type, targetId)
                    const key = `${type}:${targetId}`
                    // Baseline the re-sharing capability at the value we're
                    // about to display. We never read this principal's actual
                    // tenant-global setting (the folder's permission list only
                    // covers existing grantees), so treating the default as
                    // "unchanged" is what stops a plain grant from writing
                    // allow_sharing=true over an admin's org-wide opt-out.
                    // A deliberate flip still differs from the baseline and
                    // is still written.
                    if (!initialSharing.current.has(key)) initialSharing.current.set(key, !sharingOnFolder)
                    // A principal removed and re-added keeps its KNOWN setting:
                    // seeding the default over a loaded `false` made Share write
                    // the tenant-wide capability back on, untouched by the user.
                    const allowSharing = initialSharing.current.get(key) ?? !sharingOnFolder
                    setStaged(prev => prev.some(x => x.key === key)
                      ? prev
                      : [...prev, { key, type, targetId, label, permission: 'readcopy', allowSharing }])
                    setPicking(false)
                  }}
                  onClose={() => setPicking(false)}
                />
              )}
            </div>

            {/* Two sections, like ShareDialog's "new request" vs "Share with":
                who you're ABOUT to share with, then who already has access. */}
            <div className="px-6 pt-4 pb-2 max-h-[46vh] overflow-y-auto">
              {staged.length === 0 && (
                <>
                  <div className="text-xs themed-text-muted mb-2">{t('driveView.shareInternally.shareWith', { defaultValue: 'Share with' })}</div>
                  <div className="rounded-md border border-dashed themed-border px-4 py-6 text-center text-sm themed-text-muted">
                    {t('driveView.shareInternally.emptyHint', { defaultValue: 'No one yet — pick someone with “Add member” above.' })}
                  </div>
                </>
              )}
              {SECTIONS.map(sec => {
                const rows = staged.filter(s => sec.key === 'new' ? !s.existing : !!s.existing)
                if (rows.length === 0) return null
                return (
                  <div key={sec.key} className="mb-4 last:mb-0" data-testid={`share-internally-section-${sec.key}`}>
                    <div className="text-xs themed-text-muted mb-2 flex items-center gap-1.5">
                      {t(sec.labelKey, { defaultValue: sec.fallback })}
                      <span className="w-4 h-4 rounded-full themed-bg-primary inline-flex items-center justify-center text-[9px]">{rows.length}</span>
                    </div>
                <div className="flex flex-col gap-2">
                  {rows.map(s => (
                    <div key={s.key} className="flex items-center gap-3 px-3 py-2 themed-bg-tertiary border themed-border rounded-md" data-testid="share-internally-card">
                      {/* The SAME principal avatar Who-has-access renders. */}
                      <CardAvatar
                        tag={{ type: s.type, targetId: s.targetId, label: s.label, permission: s.permission as Permission, inherited: false }}
                        allMembers={members}
                        size={32}
                      />
                      <div className="flex-1 min-w-0">
                        {/* No per-card "has access" pill — the section heading
                            already says which group the row belongs to. */}
                        <div className="text-sm font-medium themed-text truncate">{s.label}</div>
                        {/* Second line names WHAT the row is (Role / Team) — for a
                            member the email is the more useful identifier. */}
                        <div className="text-xs themed-text-muted truncate">
                          {s.type === 'role'
                            ? t('driveView.shareInternally.tabRole', { defaultValue: 'Role' })
                            : s.type === 'team'
                              ? t('driveView.shareInternally.tabTeam', { defaultValue: 'Team' })
                              : s.targetId}
                        </div>
                      </div>
                      {/* The SHARED access popover — permission + re-sharing +
                          Remove, positioned/portaled by the component itself. */}
                      <RecipientAccessPicker
                        permission={s.permission}
                        allowSharing={s.allowSharing}
                        // Full internal tier set (incl. Manage & Edit) and no
                        // cap: the SERVER is the gate (it rejects a grant above
                        // the caller's authority). Capping the menu client-side
                        // hid tiers admins legitimately expect.
                        options={INTERNAL_TIERS}
                        // The picker fires on every click, the current option
                        // included: an existing row is dirty only when it now
                        // DIFFERS from what was granted — else a no-op re-pick
                        // enabled Share.
                        onChange={(p, allow) => setStaged(prev => prev.map(x => x.key === s.key
                          ? { ...x, permission: p, allowSharing: allow, dirty: !x.existing || p !== x.origPermission || allow !== initialSharing.current.get(x.key) } as Staged
                          : x))}
                        onRemove={() => {
                          // Dedupe by key: remove → re-add → remove again would
                          // otherwise queue the same permId twice, and the
                          // second DELETE 404s on an already-deleted row.
                          if (s.existing) setRemoved(prev => prev.some(x => x.key === s.key) ? prev : [...prev, s])
                          setStaged(prev => prev.filter(x => x.key !== s.key))
                        }}
                      />
                    </div>
                  ))}
                </div>
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <div className="flex justify-end items-center gap-3 px-6 py-4">
              <button onClick={onClose} disabled={sharing} className="text-sm themed-text-muted hover:themed-text transition-colors disabled:opacity-40">
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={() => void commit()}
                // Mirror commit()'s own guard: it early-returns on the
                // RECONCILED revoke list, so gating on raw `removed` left the
                // button live for a click that did nothing at all.
                disabled={(pending.length === 0 && revokes.length === 0) || sharing}
                className="themed-btn-primary rounded-md px-4 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="share-internally-submit"
              >
                {sharing && <Loader2 size={14} className="animate-spin" />}
                {t('driveView.shareInternally.submit', { defaultValue: 'Share' })}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
